import { plainRefs } from './refs.js';

/**
 * Das Markdown eines Textdokuments als Absätze mit einfacher Auszeichnung – Grundlage für den Vergleich von Versionen (`textCompare`)
 * und den Export (DOCX). Verweise, Bezüge und Zeitmarken werden zu ihrem Wortlaut (`plainRefs`). Verstanden wird, was der Editor
 * schreibt: Überschriften, Absätze (mit Zeilenumbrüchen), Listen, Aufgaben, Zitate, Trennlinien und Code; im Absatz fett, kursiv,
 * durchgestrichen, Code und Links (als ihr Text).
 */

export type BlockKind = 'p' | 'h1' | 'h2' | 'h3' | 'item' | 'quote' | 'rule' | 'code';

export interface TextRun {
  text: string;
  bold?: boolean;
  italic?: boolean;
  strike?: boolean;
  code?: boolean;
}

export interface TextBlock {
  kind: BlockKind;
  runs: TextRun[];
  /** Listeneintrag: Ebene ab 0, Nummer (nur nummerierte Listen), erledigt (nur Aufgaben) */
  level?: number;
  number?: number;
  done?: boolean;
}

/** Der Text eines Absatzes ohne Auszeichnung */
export const blockText = (b: TextBlock): string => b.runs.map((r) => r.text).join('');

const ESCAPABLE = /[\\`*_{}[\]()#+\-.!>~|<]/;
const WORD = /[\p{L}\p{N}]/u;
const LINK = /^!?\[([^\]\n]*)\]\(([^)\s]*)(?:\s+"[^"\n]*")?\)/;

type Style = Pick<TextRun, 'bold' | 'italic' | 'strike'>;

/** Auszeichnung im Absatz: ein Zeichen öffnet nur, wenn es später wieder schließt (sonst bleibt es stehen, wie es ist) */
function inline(src: string): TextRun[] {
  const runs: TextRun[] = [];
  const style: Style = {};
  let buf = '';
  const add = (text: string, extra: Partial<TextRun> = {}): void => {
    if (text === '') return;
    const run: TextRun = { text, ...(style.bold ? { bold: true } : {}), ...(style.italic ? { italic: true } : {}), ...(style.strike ? { strike: true } : {}), ...extra };
    const last = runs.at(-1);
    if (last && !!last.bold === !!run.bold && !!last.italic === !!run.italic && !!last.strike === !!run.strike && !!last.code === !!run.code) last.text += text;
    else runs.push(run);
  };
  const emit = (): void => {
    add(buf);
    buf = '';
  };
  /** schließt `marker` nach `from` noch einmal (nicht maskiert, direkt nach Text)? */
  const closes = (marker: string, from: number): boolean => {
    for (let j = src.indexOf(marker, from); j >= 0; j = src.indexOf(marker, j + 1)) if (src[j - 1] !== '\\' && !/\s/.test(src[j - 1] ?? ' ')) return true;
    return false;
  };
  const toggle = (key: keyof Style, marker: string, i: number): boolean => {
    // öffnen nur direkt vor Text und mit einem Gegenstück
    if (!style[key] && (/\s/.test(src[i + marker.length] ?? ' ') || !closes(marker, i + marker.length + 1))) return false;
    emit();
    style[key] = !style[key];
    return true;
  };
  for (let i = 0; i < src.length;) {
    const c = src[i]!;
    if (c === '\\' && i + 1 < src.length && ESCAPABLE.test(src[i + 1]!)) {
      buf += src[i + 1];
      i += 2;
      continue;
    }
    if (c === '`') {
      const end = src.indexOf('`', i + 1);
      if (end > i + 1) {
        emit();
        add(src.slice(i + 1, end), { code: true });
        i = end + 1;
        continue;
      }
    }
    if (c === '[' || (c === '!' && src[i + 1] === '[')) {
      const m = LINK.exec(src.slice(i));
      if (m) {
        buf += m[1];
        i += m[0].length;
        continue;
      }
    }
    const two = src.slice(i, i + 2);
    if ((two === '**' || two === '__') && toggle('bold', two, i)) {
      i += 2;
      continue;
    }
    if (two === '~~' && toggle('strike', two, i)) {
      i += 2;
      continue;
    }
    // „_“ mitten im Wort ist kein Zeichen der Auszeichnung (anna_berg)
    const intraword = c === '_' && WORD.test(src[i - 1] ?? '') && WORD.test(src[i + 1] ?? '');
    if ((c === '*' || c === '_') && !intraword && toggle('italic', c, i)) {
      i += 1;
      continue;
    }
    buf += c;
    i += 1;
  }
  emit();
  return runs;
}

/** Ende einer Zeile mit hartem Umbruch (zwei Leerzeichen bzw. „\“) */
const lineOf = (line: string): string => line.replace(/(?: {2,}|\\)$/, '');

export function textBlocks(markdown: string): TextBlock[] {
  const out: TextBlock[] = [];
  const lines = plainRefs(markdown).replace(/\r\n?/g, '\n').split('\n');
  // Zeilen des laufenden Absatzes bzw. Zitats
  let open = null as { kind: 'p' | 'quote'; lines: string[] } | null;
  const flush = (): void => {
    if (open) out.push({ kind: open.kind, runs: inline(open.lines.map(lineOf).join('\n')) });
    open = null;
  };
  const block = (b: TextBlock): void => {
    flush();
    out.push(b);
  };
  for (let i = 0; i < lines.length; i++) {
    const line = lines[i]!;
    const fence = /^\s*(```|~~~)/.exec(line);
    if (fence) {
      const body: string[] = [];
      while (++i < lines.length && !lines[i]!.trimStart().startsWith(fence[1]!)) body.push(lines[i]!);
      block({ kind: 'code', runs: body.length > 0 ? [{ text: body.join('\n'), code: true }] : [] });
      continue;
    }
    if (line.trim() === '') {
      flush();
      continue;
    }
    let m: RegExpExecArray | null;
    if ((m = /^\s{0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/.exec(line))) {
      block({ kind: m[1]!.length === 1 ? 'h1' : m[1]!.length === 2 ? 'h2' : 'h3', runs: inline(m[2]!) });
      continue;
    }
    if (/^\s{0,3}([-*_])(?:\s*\1){2,}\s*$/.test(line)) {
      block({ kind: 'rule', runs: [] });
      continue;
    }
    if ((m = /^(\s*)(?:[-*+]|(\d+)[.)])\s+(?:\[([ xX])\]\s+)?(.*)$/.exec(line))) {
      block({
        kind: 'item',
        runs: inline(m[4]!),
        level: Math.floor(m[1]!.replace(/\t/g, '  ').length / 2),
        ...(m[2] !== undefined ? { number: Number(m[2]) } : {}),
        ...(m[3] !== undefined ? { done: m[3] !== ' ' } : {}),
      });
      continue;
    }
    const quoted = /^\s{0,3}>\s?(.*)$/.exec(line);
    const kind = quoted ? 'quote' : 'p';
    const text = quoted ? quoted[1]! : line;
    // „>“ allein trennt Absätze in einem Zitat
    if (kind === 'quote' && text.trim() === '') {
      flush();
      continue;
    }
    if (open?.kind !== kind) flush();
    open ??= { kind, lines: [] };
    open.lines.push(text);
  }
  flush();
  return out;
}
